import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaDollarSign, FaMusic } from 'react-icons/fa';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import NotificationPreferences from './partial/NotificationPreferences.jsx';
import { formatRelativeTime } from './partial/home/HomeNotificationsCard.jsx';
import { useNotificationsContext } from '../contexts/NotificationsContext.jsx';
import { FEED_STATUS } from '../hooks/useNotifications.js';
import { buildNotifications } from '../hooks/useListenerDashboard.js';
import { formatUnreadBadge, mergeActivity, notificationTile } from '../_helpers/notifications.js';
import NotificationTile from './partial/NotificationTile.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { fetchWithFallback } from '../_helpers/listenerActivity.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import { englishT } from '../locales/index.js';
import config from '../config.js';
import styles from '../styles/NotificationsPage.module.css';

/* English copy of the page's fixed messages, for tests and callers without a locale. */
export const UNAVAILABLE_TEXT = englishT('notifications.unavailable');
export const UNAUTHORIZED_TEXT = englishT('notifications.unauthorized');
export const EMPTY_TEXT = englishT('notifications.empty');
export const ACTIVITY_ERROR_TEXT = englishT('notifications.activityError');
export const LINK_UNAVAILABLE_TEXT = englishT('notifications.row.linkUnavailable');

/** Catalog key of the notice for each degraded feed connection state; none while connected. */
const CONNECTION_KEYS = {
  reconnecting: 'notifications.connection.reconnecting',
  polling: 'notifications.connection.polling',
};

/** How often the relative times ("1m", "2h") in the rows are recomputed. */
export const RELATIVE_TIME_TICK_MS = 60000;

/** A row from the backend feed (as opposed to request/tip activity): it carries the id to mark read by. */
const isFeedRow = (item) => item.notificationId != null;

function Avatar({ item }) {
  if (isFeedRow(item)) {
    // The backend's visual (ENG-889): cover art, band image or the tip amount.
    return (
      <NotificationTile
        tile={item.tile ?? notificationTile(item)}
        type={item.kind}
        className={styles.avatar}
        emptyClassName={styles.avatarFallback}
        moneyClassName={styles.avatarMoney}
      />
    );
  }
  const Icon = item.kind === 'tip' ? FaDollarSign : FaMusic;
  return <span className={`${styles.avatar} ${styles.avatarFallback}`} aria-hidden="true"><Icon /></span>;
}

/**
 * A backend feed notification row (a live stream, or any other type the
 * backend renders — ENG-382): its label and detail as `describeNotification`
 * rendered them in the active locale (the structured message or the type
 * template where the client can, else the backend's title/body), when it
 * arrived, and its read state. The label is
 * the link when the backend gave a destination; otherwise the row still
 * renders, marked "Link unavailable", so nothing is lost or guessed.
 */
function FeedNotificationRow({ item, now, pending, onMarkRead }) {
  const { t, format } = useLocale();
  const { detail } = item;
  const markRead = () => onMarkRead(item.notificationId);

  const renderLabel = () => {
    if (item.destination?.kind === 'route') {
      return <Link to={item.destination.to} className={styles.rowLink} onClick={markRead}>{item.label}</Link>;
    }
    if (item.destination?.kind === 'url') {
      return (
        <a href={item.destination.href} className={styles.rowLink} target="_blank" rel="noopener noreferrer" onClick={markRead}>
          {item.label}
        </a>
      );
    }
    return (
      <span className={styles.rowTitle}>
        {item.label}
        <span className={styles.rowNote}> · {t('notifications.row.linkUnavailable')}</span>
      </span>
    );
  };

  return (
    <li className={`${styles.row}${item.read ? '' : ` ${styles.rowUnread}`}`} data-testid={`notification-${item.notificationId}`} data-read={item.read ? 'true' : 'false'}>
      <Avatar item={item} />
      <div className={styles.rowMain}>
        {renderLabel()}
        {detail && <span className={styles.rowSub}>{detail}</span>}
      </div>
      <div className={styles.rowMeta}>
        <time className={styles.rowTime} dateTime={item.at.toISOString()}>{formatRelativeTime(item.at, now, { t, format })}</time>
        {!item.read && (
          <>
            <span className={styles.unreadDot} aria-hidden="true" />
            <button
              type="button"
              className={styles.textButton}
              aria-label={t('notifications.row.markReadLabel', { label: item.label })}
              disabled={pending}
              onClick={markRead}
            >
              {t('notifications.row.markRead')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/** A request/tip activity row, exactly as the home card shows it, linking to the history it belongs to. */
function ActivityRow({ item, now }) {
  const { t, format } = useLocale();
  // The row text is the item's catalog descriptor when it carries one, so it follows the active locale.
  const label = item.message ? t(item.message.key, item.message.params) : item.label;
  return (
    <li className={styles.row} data-testid={item.id}>
      <Avatar item={item} />
      <div className={styles.rowMain}>
        <Link to="/history" className={styles.rowLink}>{label}</Link>
        {item.bandName && item.kind === 'request' && <span className={styles.rowSub}>{item.bandName}</span>}
      </div>
      <div className={styles.rowMeta}>
        <time className={styles.rowTime} dateTime={item.at.toISOString()}>{formatRelativeTime(item.at, now, { t, format })}</time>
      </div>
    </li>
  );
}

/**
 * The listener's notification inbox at /notifications (ENG-26): live-stream
 * alerts from the backend feed interleaved with the request/tip activity the
 * home card already shows, the backend unread count, per-item and mark-all
 * read, cursor paging, and the listener's notification settings. /history is
 * untouched; activity rows link back to it.
 */
function NotificationsPage() {
  const navigate = useNavigate();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const { t, locale } = useLocale();
  // `t`/`locale` through a ref so the activity load effect does not refetch on a language switch.
  const localeRef = useRef({ t, locale });
  localeRef.current = { t, locale };
  const feed = useNotificationsContext();
  const {
    items,
    unread,
    status,
    error,
    hasMore,
    loadingMore,
    loadMoreError,
    mutationError,
    pendingIds,
    connectionStatus,
    announcement,
    hasSession,
    reload,
    loadMore,
    markRead,
    markAllRead,
    dismissMutationError,
  } = feed;

  const unauthorized = !hasSession || status === FEED_STATUS.UNAUTHORIZED;

  const [activity, setActivity] = useState({ items: [], loading: true, error: false });

  // The moment the rows' relative times are measured from, moved on once a
  // minute so an inbox left open does not keep saying "1m", and again
  // whenever the rows change so a live arrival is measured from now, not
  // from the last tick.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), RELATIVE_TIME_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (unauthorized) {
      setActivity({ items: [], loading: false, error: false });
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const [requests, tips] = await Promise.allSettled([
        fetchWithFallback(fetchWrapperRef.current, `${config.apiBaseUrl}/user/requests`, 'requests'),
        fetchWithFallback(fetchWrapperRef.current, `${config.apiBaseUrl}/user/tips`, 'tips'),
      ]);
      if (cancelled) return;
      const failed = requests.status === 'rejected' && tips.status === 'rejected';
      const built = buildNotifications(
        requests.status === 'fulfilled' ? requests.value : [],
        tips.status === 'fulfilled' ? tips.value : [],
        new Date(),
        null,
        Infinity,
        localeRef.current
      );
      setActivity({ items: built.items, loading: false, error: failed });
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [unauthorized]);

  const rows = useMemo(() => mergeActivity(items, activity.items, Infinity, t, locale), [items, activity.items, t, locale]);
  useEffect(() => {
    setNow(new Date());
  }, [rows]);
  const badge = formatUnreadBadge(unread);
  const loading = status === FEED_STATUS.LOADING || activity.loading;
  const canMarkAll = status === FEED_STATUS.READY && (unread.count > 0 || unread.capped || items.some((item) => !item.read));

  const renderBody = () => {
    if (unauthorized) {
      return (
        <div className={styles.stateContainer} data-testid="notifications-unauthorized">
          <p className={styles.emptyText}>{t('notifications.unauthorized')}</p>
          <button type="button" className={styles.actionButton} onClick={() => navigate('/login')}>{t('common.actions.signIn')}</button>
        </div>
      );
    }
    if (loading) {
      return (
        <div className={styles.stateContainer} data-testid="notifications-loading">
          <LoadingSpinner />
        </div>
      );
    }

    const notices = (
      <>
        {status === FEED_STATUS.ERROR && (
          <div className={styles.inlineError} role="alert" data-testid="notifications-error">
            <span>{error}</span>
            <div className={styles.inlineActions}>
              <button type="button" className={styles.secondaryButton} onClick={reload}>{t('common.actions.tryAgain')}</button>
            </div>
          </div>
        )}
        {status === FEED_STATUS.UNAVAILABLE && (
          <p className={styles.notice} role="status" data-testid="notifications-unavailable">{t('notifications.unavailable')}</p>
        )}
        {activity.error && (
          <p className={styles.notice} role="status" data-testid="activity-error">{t('notifications.activityError')}</p>
        )}
        {CONNECTION_KEYS[connectionStatus] && (
          <p className={styles.mutedText} role="status" data-testid="connection-status">{t(CONNECTION_KEYS[connectionStatus])}</p>
        )}
      </>
    );

    if (rows.length === 0) {
      return (
        <>
          {notices}
          <div className={styles.stateContainer} data-testid="notifications-empty">
            <p className={styles.emptyText}>{t('notifications.empty')}</p>
            <button type="button" className={styles.actionButton} onClick={() => navigate('/performers')}>{t('common.actions.findPerformers')}</button>
          </div>
        </>
      );
    }

    return (
      <>
        {notices}
        <ul className={styles.list} aria-label={t('notifications.title')}>
          {rows.map((item) =>
            isFeedRow(item) ? (
              <FeedNotificationRow
                key={item.id}
                item={item}
                now={now}
                pending={pendingIds.has(item.notificationId)}
                onMarkRead={markRead}
              />
            ) : (
              <ActivityRow key={item.id} item={item} now={now} />
            )
          )}
        </ul>
        {loadMoreError && <p className={styles.errorText} role="alert">{loadMoreError}</p>}
        {hasMore && (
          <div className={styles.loadMore}>
            <button type="button" className={styles.secondaryButton} onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t('common.status.loadingEllipsis') : t('notifications.loadMore')}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="home-page">
      <div className="home-page-wrapper">
        <StreamPageHeader />
        <div className={styles.page}>
          <header className={styles.header}>
            <div className={styles.headerRow}>
              <h1 className={styles.title}>
                {t('notifications.title')}
                {badge && (
                  <span className={styles.unreadChip} data-testid="unread-chip">
                    {t('notifications.unreadChip', { badge })}
                  </span>
                )}
              </h1>
              {!unauthorized && (
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={markAllRead}
                  disabled={!canMarkAll}
                  data-testid="mark-all-read"
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
            <p className={styles.subtitle}>{t('notifications.subtitle')}</p>
          </header>

          {/* Live arrivals are announced here; keyed so a repeat announces again. */}
          <div className={styles.visuallyHidden} role="status" aria-live="polite" aria-atomic="true" data-testid="live-region">
            {announcement ? <span key={announcement.id}>{t('notifications.liveAnnouncement', { text: announcement.text })}</span> : null}
          </div>

          {mutationError && (
            <div className={styles.inlineError} role="alert" data-testid="mutation-error">
              <span>{mutationError.message}</span>
              <div className={styles.inlineActions}>
                <button type="button" className={styles.secondaryButton} onClick={mutationError.retry}>{t('common.actions.retry')}</button>
                <button type="button" className={styles.textButton} onClick={dismissMutationError}>{t('common.actions.dismiss')}</button>
              </div>
            </div>
          )}

          {renderBody()}

          {!unauthorized && <NotificationPreferences enabled={!unauthorized} />}
        </div>
      </div>
    </div>
  );
}

export default NotificationsPage;
